<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="sales-tickets__header">
        <div class="sales-tickets__title">Tickets</div>
        <div class="sales-tickets__actions">
          <v-btn
            outlined
            color="secondary"
            class="sales-tickets__action-btn"
            :disabled="!canEditSelected"
            @click="openOperations('edit')"
          >
            <v-icon left small>mdi-pencil</v-icon>
            Editar
          </v-btn>
          <v-btn
            outlined
            color="error"
            class="sales-tickets__action-btn"
            :disabled="!canRefundSelected"
            @click="openOperations('refund')"
          >
            <v-icon left small>mdi-cash-refund</v-icon>
            Devolver
          </v-btn>
          <v-btn
            outlined
            color="primary"
            class="sales-tickets__action-btn"
            :disabled="!selectedTicket"
            @click="openReprint"
          >
            <v-icon left small>mdi-printer</v-icon>
            Reimprimir
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="selectedTicket && selectedCapabilities"
          dense
          text
          class="mb-3"
          :type="selectedCapabilities.hasRefunds ? 'info' : 'success'"
        >
          Ticket #{{ selectedTicket.ticketNumber }} seleccionado.
          <span v-if="selectedCapabilities.hasRefunds">
            Ya tiene devoluciones. Disponible: {{ formatQty(selectedCapabilities.totalAvailable) }}.
          </span>
          <span v-else>
            Sin devoluciones previas. Disponible: {{ formatQty(selectedCapabilities.totalAvailable) }}.
          </span>
          <span v-if="Number(selectedTicket.ticketType) === 1">
            Las devoluciones no se pueden editar.
          </span>
          <span v-else-if="!canEditSelected && Number(selectedTicket.ticketType) !== 2">
            Edición no disponible.
          </span>
        </v-alert>
        <v-alert v-else dense text type="info" class="mb-3">
          Seleccione un ticket para Editar, Devolver o Reimprimir. Clic en la fila para ver el detalle.
        </v-alert>

        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="search"
              label="Buscar por ticket, folio o cliente"
              append-icon="mdi-magnify"
              outlined
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model="dateFrom"
              label="Fecha inicio"
              type="date"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model="dateTo"
              label="Fecha fin"
              type="date"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="host"
              :items="cashRegisters"
              item-text="name"
              item-value="id"
              label="Caja"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="ticketType"
              :items="ticketTypeFilters"
              item-text="label"
              item-value="value"
              label="Tipo"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="1" class="d-flex align-center">
            <v-btn text color="primary" @click="resetFilters">Limpiar</v-btn>
          </v-col>
        </v-row>

        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="tickets"
          :server-items-length="totalItems"
          :options.sync="options"
          :loading="loading"
          show-select
          single-select
          item-key="receiptId"
          loading-text="Cargando tickets..."
          no-data-text="No se encontraron tickets"
          class="elevation-1"
          @click:row="onRowClick"
        >
          <template #[`item.dateNew`]="{ item }">
            {{ formatDate(item.dateNew) }}
          </template>
          <template #[`item.ticketType`]="{ item }">
            <v-chip x-small dark :color="typeColor(item.ticketType)">
              {{ item.ticketTypeLabel || typeLabel(item.ticketType) }}
            </v-chip>
          </template>
          <template #[`item.productsCount`]="{ item }">
            {{ Number(item.productsCount || 0).toFixed(2) }}
          </template>
          <template #[`item.total`]="{ item }">
            ${{ Number(item.total || 0).toFixed(2) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="detailDialog" max-width="960px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            Detalle de ticket {{ selectedTicketDetail.ticket?.ticketNumber || '-' }}
            <v-chip
              v-if="selectedTicketDetail.ticket"
              x-small
              dark
              class="ml-2"
              :color="typeColor(selectedTicketDetail.ticket.ticketType)"
            >
              {{ selectedTicketDetail.ticket.ticketTypeLabel || typeLabel(selectedTicketDetail.ticket.ticketType) }}
            </v-chip>
          </span>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense v-if="selectedTicketDetail.ticket">
            <v-col cols="12" md="3">
              <strong>Cliente:</strong> {{ selectedTicketDetail.ticket.customerName }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>Caja:</strong> {{ selectedTicketDetail.ticket.host || '-' }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>Productos:</strong>
              {{ Number(selectedTicketDetail.ticket.productsCount || 0).toFixed(2) }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>Total:</strong>
              ${{ Number(selectedTicketDetail.ticket.total || 0).toFixed(2) }}
            </v-col>
          </v-row>

          <v-alert
            v-if="selectedTicketDetail.capabilities"
            dense
            text
            class="mt-3"
            :type="selectedTicketDetail.capabilities.hasRefunds ? 'info' : 'success'"
          >
            <span v-if="selectedTicketDetail.capabilities.hasRefunds">
              Este ticket ya tiene devoluciones asociadas.
              Disponible total: {{ formatQty(selectedTicketDetail.capabilities.totalAvailable) }}.
            </span>
            <span v-else>
              Sin devoluciones previas.
              Disponible total: {{ formatQty(selectedTicketDetail.capabilities.totalAvailable) }}.
            </span>
          </v-alert>

          <v-data-table
            :headers="detailHeaders"
            :items="selectedTicketDetail.lines"
            :loading="loadingDetail"
            dense
            disable-pagination
            hide-default-footer
            no-data-text="Sin líneas de ticket"
            class="mt-3"
          >
            <template #[`item.units`]="{ item }">
              {{ Number(item.units || 0).toFixed(2) }}
            </template>
            <template #[`item.sold`]="{ item }">
              {{ item.sold != null ? Number(item.sold).toFixed(3) : '-' }}
            </template>
            <template #[`item.refunded`]="{ item }">
              {{ item.refunded != null ? Number(item.refunded).toFixed(3) : '-' }}
            </template>
            <template #[`item.available`]="{ item }">
              {{ item.available != null ? Number(item.available).toFixed(3) : '-' }}
            </template>
            <template #[`item.price`]="{ item }">
              ${{ Number(item.price || 0).toFixed(2) }}
            </template>
            <template #[`item.subtotal`]="{ item }">
              ${{ Number(item.subtotal || 0).toFixed(2) }}
            </template>
          </v-data-table>

          <div class="subtitle-2 mt-4 mb-2">Pagos</div>
          <v-data-table
            :headers="paymentHeaders"
            :items="selectedTicketDetail.payments || []"
            dense
            disable-pagination
            hide-default-footer
            no-data-text="Sin pagos"
          >
            <template #[`item.paymentType`]="{ item }">
              {{ transactionPaymentLabel(item.paymentType) }}
            </template>
            <template #[`item.total`]="{ item }">
              ${{ Number(item.total || 0).toFixed(2) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            color="primary"
            :disabled="!selectedTicketDetail.ticket"
            @click="openReprintFromDetail"
          >
            Reimprimir
          </v-btn>
          <v-spacer />
          <v-btn
            text
            color="error"
            :disabled="!(selectedTicketDetail.capabilities && selectedTicketDetail.capabilities.canRefund)"
            @click="openOperationsFromDetail('refund')"
          >
            Devolver
          </v-btn>
          <v-btn
            text
            color="secondary"
            :disabled="!(selectedTicketDetail.capabilities && selectedTicketDetail.capabilities.canEdit)"
            @click="openOperationsFromDetail('edit')"
          >
            Editar
          </v-btn>
          <v-btn text @click="detailDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ticket-reprint-dialog
      v-model="reprintDialog"
      :receipt-id="reprintReceiptId"
      :ticket-number="reprintTicketNumber"
      @printed="onPrinted"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import * as salesService from '@/services/salesService';
import { fetchPosTicketCapabilities } from '@/services/posSalesService';
import TicketReprintDialog from '@/components/sales/TicketReprintDialog.vue';
import { formatDateTimeByTimezone } from '@/utils/dateTime';
import { transactionPaymentLabel } from '@/utils/legacyPosLabels';
import { ticketTypeColor, ticketTypeLabel } from '@/utils/ticketRefundLogic';

/**
 * Módulo Tickets: historial para consultar y seleccionar un ticket.
 * Las operaciones Editar/Devolver abren el módulo operativo tipo POS;
 * Reimprimir permite elegir navegador o puente.
 */
export default {
  name: 'SalesTicketsView',
  components: {
    TicketReprintDialog,
  },
  data() {
    return {
      loading: false,
      search: '',
      dateFrom: '',
      dateTo: '',
      host: null,
      ticketType: null,
      cashRegisters: [],
      debounceTimer: null,
      options: {
        page: 1,
        itemsPerPage: 20,
      },
      tickets: [],
      totalItems: 0,
      selected: [],
      selectedCapabilities: null,
      loadingCapabilities: false,
      capabilitiesRequestSeq: 0,
      detailDialog: false,
      loadingDetail: false,
      detailRequestSeq: 0,
      detailReceiptId: null,
      reprintDialog: false,
      reprintReceiptId: null,
      reprintTicketNumber: null,
      selectedTicketDetail: {
        ticket: null,
        lines: [],
        payments: [],
        capabilities: null,
      },
      snackbar: {
        show: false,
        message: '',
        color: 'success',
      },
      ticketTypeFilters: [
        { label: 'Ventas', value: 0 },
        { label: 'Devoluciones', value: 1 },
        { label: 'Pagos', value: 2 },
      ],
      headers: [
        { text: 'Fecha', value: 'dateNew', sortable: false },
        { text: 'Tipo', value: 'ticketType', sortable: false },
        { text: 'Folio', value: 'ticketNumber', sortable: false },
        { text: 'Cliente', value: 'customerName', sortable: false },
        { text: 'Productos', value: 'productsCount', sortable: false, align: 'end' },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
      ],
      detailHeaders: [
        { text: '#', value: 'line', sortable: false, width: '60px' },
        { text: 'Producto', value: 'productName', sortable: false },
        { text: 'Cantidad', value: 'units', sortable: false, align: 'end' },
        { text: 'Vendido', value: 'sold', sortable: false, align: 'end' },
        { text: 'Devuelto', value: 'refunded', sortable: false, align: 'end' },
        { text: 'Disponible', value: 'available', sortable: false, align: 'end' },
        { text: 'Precio', value: 'price', sortable: false, align: 'end' },
        { text: 'Subtotal', value: 'subtotal', sortable: false, align: 'end' },
      ],
      paymentHeaders: [
        { text: 'Método', value: 'paymentType', sortable: false },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    selectedTicket() {
      return this.selected.length ? this.selected[0] : null;
    },
    canEditSelected() {
      if (!this.selectedTicket) return false;
      // Solo tickets de venta (no devoluciones ni pagos).
      if (Number(this.selectedTicket.ticketType) !== 0) return false;
      if (this.selectedCapabilities) {
        return Boolean(this.selectedCapabilities.canEdit);
      }
      return this.selectedTicket.cashActive !== false;
    },
    canRefundSelected() {
      if (!this.selectedTicket || !this.selectedCapabilities) {
        return Boolean(this.selectedTicket && Number(this.selectedTicket.ticketType) === 0);
      }
      return Boolean(this.selectedCapabilities.canRefund);
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchTickets();
      },
    },
    search() {
      this.debounceFetch();
    },
    dateFrom() {
      this.debounceFetch();
    },
    dateTo() {
      this.debounceFetch();
    },
    host() {
      this.debounceFetch();
    },
    ticketType() {
      this.debounceFetch();
    },
    selected(value) {
      if (!value || !value.length) {
        this.capabilitiesRequestSeq += 1;
        this.selectedCapabilities = null;
        return;
      }
      if (this.detailDialog && this.detailReceiptId === value[0].receiptId) return;
      this.loadSelectedCapabilities(value[0].receiptId);
    },
    detailDialog(value) {
      if (!value) {
        this.detailRequestSeq += 1;
        this.detailReceiptId = null;
        this.loadingDetail = false;
      }
    },
  },
  mounted() {
    this.loadCashRegisters();
    this.fetchTickets();
  },
  methods: {
    transactionPaymentLabel,
    typeLabel: ticketTypeLabel,
    typeColor: ticketTypeColor,
    formatDate(value) {
      return formatDateTimeByTimezone(value, '-');
    },
    formatQty(value) {
      return Number(value || 0).toFixed(3);
    },
    notify(message, color = 'success') {
      this.snackbar = { show: true, message, color };
      this.$emit('show-alert', { message, type: color === 'error' ? 'error' : 'success' });
    },
    debounceFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.fetchTickets();
      }, 350);
    },
    resetFilters() {
      this.search = '';
      this.dateFrom = '';
      this.dateTo = '';
      this.host = null;
      this.ticketType = null;
      this.options.page = 1;
      this.fetchTickets();
    },
    async loadCashRegisters() {
      try {
        this.cashRegisters = await salesService.fetchCashRegisters();
      } catch (error) {
        this.notify('Error al cargar cajas.', 'error');
      }
    },
    async fetchTickets() {
      this.loading = true;
      try {
        const data = await salesService.fetchSalesTickets({
          ...this.options,
          q: this.search || undefined,
          dateFrom: this.dateFrom || undefined,
          dateTo: this.dateTo || undefined,
          host: this.host || undefined,
          ticketType: this.ticketType == null ? undefined : this.ticketType,
        });
        this.tickets = data.tickets || [];
        this.totalItems = data.totalCount || 0;
        // Mantener selección si el ticket sigue en la página.
        if (this.selectedTicket) {
          const stillThere = this.tickets.find(
            (item) => item.receiptId === this.selectedTicket.receiptId,
          );
          this.selected = stillThere ? [stillThere] : [];
        }
      } catch (error) {
        this.tickets = [];
        this.totalItems = 0;
        this.notify('Error al cargar tickets de venta.', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadSelectedCapabilities(receiptId) {
      const requestSeq = ++this.capabilitiesRequestSeq;
      this.loadingCapabilities = true;
      try {
        const data = await fetchPosTicketCapabilities(receiptId);
        if (
          requestSeq !== this.capabilitiesRequestSeq
          || !this.selectedTicket
          || this.selectedTicket.receiptId !== receiptId
        ) return;
        this.selectedCapabilities = data.capabilities || null;
      } catch (error) {
        if (requestSeq !== this.capabilitiesRequestSeq) return;
        this.selectedCapabilities = null;
      } finally {
        if (requestSeq === this.capabilitiesRequestSeq) {
          this.loadingCapabilities = false;
        }
      }
    },
    onRowClick(item) {
      // En Vuetify 2 click:row puede recibir (item) o ({ item }).
      const ticket = item && item.receiptId ? item : (item && item.item) || null;
      if (!ticket) return;
      this.selectedCapabilities = null;
      this.selected = [ticket];
      this.openTicketDetail(ticket);
    },
    async openTicketDetail(ticket) {
      const requestSeq = ++this.detailRequestSeq;
      this.capabilitiesRequestSeq += 1;
      this.detailReceiptId = ticket.receiptId;
      this.detailDialog = true;
      this.loadingDetail = true;
      this.selectedTicketDetail = {
        ticket: null,
        lines: [],
        payments: [],
        capabilities: null,
      };
      try {
        const detail = await salesService.fetchSalesTicketDetail(ticket.receiptId);
        if (
          requestSeq !== this.detailRequestSeq
          || this.detailReceiptId !== ticket.receiptId
        ) return;
        this.selectedTicketDetail = detail;
        this.selectedCapabilities = this.selectedTicketDetail.capabilities || null;
      } catch (error) {
        if (requestSeq !== this.detailRequestSeq) return;
        this.notify('Error al cargar detalle del ticket.', 'error');
      } finally {
        if (requestSeq === this.detailRequestSeq) {
          this.loadingDetail = false;
        }
      }
    },
    openOperations(mode) {
      if (!this.selectedTicket) return;
      this.$router.push({
        name: 'AdminTicketOperations',
        params: {
          receiptId: this.selectedTicket.receiptId,
          mode,
        },
      });
    },
    openOperationsFromDetail(mode) {
      const ticket = this.selectedTicketDetail.ticket;
      if (!ticket || !ticket.receiptId) return;
      this.detailDialog = false;
      this.$router.push({
        name: 'AdminTicketOperations',
        params: {
          receiptId: ticket.receiptId,
          mode,
        },
      });
    },
    openReprint() {
      if (!this.selectedTicket) return;
      this.reprintReceiptId = this.selectedTicket.receiptId;
      this.reprintTicketNumber = this.selectedTicket.ticketNumber;
      this.reprintDialog = true;
    },
    openReprintFromDetail() {
      const ticket = this.selectedTicketDetail.ticket;
      if (!ticket) return;
      this.reprintReceiptId = ticket.receiptId;
      this.reprintTicketNumber = ticket.ticketNumber;
      this.reprintDialog = true;
    },
    onPrinted({ mode, outputMode }) {
      const label = outputMode === 'document' ? 'Documento' : 'Ticket';
      this.notify(mode === 'browser'
        ? `Ventana de impresión abierta para ${label.toLowerCase()}.`
        : `${label} enviado al puente de impresión.`);
    },
  },
};
</script>

<style scoped>
.sales-tickets__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.sales-tickets__title {
  flex: 1 1 auto;
  min-width: 0;
}

.sales-tickets__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
  max-width: 420px;
}

.sales-tickets__action-btn {
  width: 100%;
  margin: 0 !important;
}

.sales-tickets__actions .sales-tickets__action-btn:last-child {
  grid-column: 1 / -1;
}

@media (max-width: 600px) {
  .sales-tickets__header {
    flex-direction: column;
    align-items: stretch;
  }

  .sales-tickets__actions {
    max-width: none;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .sales-tickets__actions {
    max-width: none;
    flex: 1 1 100%;
  }
}
</style>
